import React, { useState, useEffect } from 'react';
import axiosInstance from '../../utils/axiosInstance';

const S_CFG = {
  pending: { label: 'Pending', color: '#B45309', bg: '#FFFBEB', border: '#FDE68A' },
  assigned: { label: 'Assigned', color: '#2563EB', bg: '#EFF6FF', border: '#BFDBFE' },
  delivered: { label: 'Delivered', color: '#16A34A', bg: '#F0FDF4', border: '#BBF7D0' },
  cancelled: { label: 'Cancelled', color: '#DC2626', bg: '#FEF2F2', border: '#FECACA' },
};
const P_CFG = {
  completed: { label: 'Paid', color: '#16A34A', bg: '#F0FDF4' },
  pending: { label: 'Pending', color: '#B45309', bg: '#FFFBEB' },
  failed: { label: 'Failed', color: '#DC2626', bg: '#FEF2F2' },
};
const LIMIT = 10;

const OrderList = () => {
  const [orders, setOrders] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [deliveryBoys, setDeliveryBoys] = useState([]);
  const [page, setPage] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState({ text: '', type: '' });
  const [expanded, setExpanded] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [exporting, setExporting] = useState(false);
  const [assigningOrder, setAssigningOrder] = useState(null);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/auth/orders');
      const data = res.data.orders || [];
      setOrders(data);
      setTotalOrders(res.data.totalOrders || data.length);
    } catch {
      setMsg({ text: 'Failed to fetch orders.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchDeliveryBoys = async () => {
    try {
      const res = await axiosInstance.get('/delivery/delivery-boys');
      const data = res.data.deliveryBoys || res.data.data || res.data || [];
      setDeliveryBoys(Array.isArray(data) ? data : []);
    } catch {
      /* silent */
    }
  };

  useEffect(() => {
    fetchOrders();
    fetchDeliveryBoys();
  }, []);

  useEffect(() => {
    let r = [...orders];
    if (statusFilter !== 'all') r = r.filter(o => o.orderStatus === statusFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      r = r.filter(o =>
        o.orderId?.toLowerCase().includes(q) ||
        o.orderPlacedBy?.name?.toLowerCase().includes(q) ||
        o.orderPlacedBy?.email?.toLowerCase().includes(q) ||
        o.orderPlacedBy?.phone?.includes(q)
      );
    }
    setFiltered(r);
    setPage(1);
  }, [orders, statusFilter, search]);

  const paginated = filtered.slice((page - 1) * LIMIT, page * LIMIT);
  const totalPages = Math.ceil(filtered.length / LIMIT);
  const pageNums = () => {
    const max = 5, start = Math.max(1, page - 2);
    const end = Math.min(totalPages, start + max - 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const handleAssignDelivery = async (orderId, deliveryBoyId) => {
    if (!deliveryBoyId) return;
    try {
      await axiosInstance.put('/vendor/assign-delivery', { orderId, deliveryBoyId });
      setMsg({ text: 'Delivery partner assigned successfully!', type: 'success' });
      setAssigningOrder(null);
      fetchOrders();
    } catch (err) {
      setMsg({ text: err.response?.data?.message || 'Failed to assign delivery partner', type: 'error' });
    }
  };

  const exportCSV = async () => {
    setExporting(true);
    try {
      const headers = ['Order ID', 'Customer', 'Email', 'Phone', 'Products', 'Total', 'Payment Method', 'Payment Status', 'Order Status', 'Delivery Boy', 'Date'];
      const rows = filtered.map(o => [
        o.orderId, o.orderPlacedBy?.name || '-', o.orderPlacedBy?.email || '-', o.orderPlacedBy?.phone || '-',
        o.products?.map(p => `${p.productName} x${p.quantity}`).join(' | '),
        o.totalAmount, o.paymentMethod?.toUpperCase(), o.paymentStatus, o.orderStatus,
        o.deliveryBoy?.name || 'Not Assigned', new Date(o.createdAt).toLocaleDateString('en-IN'),
      ]);
      const csv = [headers, ...rows].map(r => r.map(v => `"${v}"`).join(',')).join('\n');
      const a = Object.assign(document.createElement('a'), { href: URL.createObjectURL(new Blob([csv], { type: 'text/csv' })), download: `orders_${new Date().toISOString().slice(0, 10)}.csv` });
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
    } catch { setMsg({ text: 'Export failed.', type: 'error' }); }
    finally { setExporting(false); }
  };

  const counts = ['pending', 'assigned', 'delivered', 'cancelled'].reduce((a, s) => ({ ...a, [s]: orders.filter(o => o.orderStatus === s).length }), {});

  return (
    <div className="orders-page">
      <div className="ph">
        <div>
          <h1>Orders</h1>
          <p>{totalOrders} total orders</p>
        </div>
        <div className="ph-actions">
          <button className="btn btn-outline btn-sm" onClick={exportCSV} disabled={exporting}>
            <i className="fas fa-download" /> {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
        </div>
      </div>

      {/* Status filter tabs */}
      <div className="status-tabs">
        {[
          { key: 'all', label: 'All', count: orders.length },
          { key: 'pending', label: 'Pending', count: counts.pending },
          { key: 'assigned', label: 'Assigned', count: counts.assigned },
          { key: 'delivered', label: 'Delivered', count: counts.delivered },
          { key: 'cancelled', label: 'Cancelled', count: counts.cancelled },
        ].map(s => (
          <button key={s.key} className={`status-tab${statusFilter === s.key ? ' on' : ''}`} onClick={() => setStatusFilter(s.key)}>
            {s.label} <span className="tab-count">{s.count}</span>
          </button>
        ))}
      </div>

      {/* Search + actions */}
      <div className="toolbar">
        <div className="search-box">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
          <input placeholder="Search order ID, customer, phone…" value={search} onChange={e => setSearch(e.target.value)} />
          {search && <button className="clear-btn" onClick={() => setSearch('')}>✕</button>}
        </div>
      </div>

      {msg.text && (
        <div className={`alert alert-${msg.type === 'success' ? 'success' : 'error'}`} style={{ marginBottom: 12 }}>
          {msg.text}
          <button className="alert-close" onClick={() => setMsg({ text: '', type: '' })}>✕</button>
        </div>
      )}

      {/* Table */}
      <div className="tbl-wrap">
        {loading ? (
          <div className="loading-state"><div className="spinner" /><p>Loading orders…</p></div>
        ) : paginated.length === 0 ? (
          <div className="empty-state"><i className="fas fa-receipt" style={{ fontSize: 32, color: 'var(--t3)' }} /><p>No orders found</p></div>
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>#</th><th>Order ID</th><th>Customer</th><th>Products</th>
                <th>Total</th><th>Payment</th><th>Status</th><th>Delivery</th><th>Date</th><th></th>
              </tr>
            </thead>
            <tbody>
              {paginated.map((o, i) => {
                const sc = S_CFG[o.orderStatus] || S_CFG.pending;
                const pc = P_CFG[o.paymentStatus] || P_CFG.pending;
                const isExp = expanded === o.orderId;
                const custAddr = o.orderPlacedBy?.address;
                const addrStr = typeof custAddr === 'object'
                  ? [custAddr.street, custAddr.city, custAddr.state, custAddr.pincode].filter(Boolean).join(', ')
                  : (custAddr || '—');

                return (
                  <React.Fragment key={o.orderId || o._id}>
                    <tr>
                      <td className="col-num">{(page - 1) * LIMIT + i + 1}</td>
                      <td><span className="order-id-cell">#{o.orderId?.slice(-8).toUpperCase()}</span></td>
                      <td>
                        <div className="cust-cell">
                          <div className="cust-av">{(o.orderPlacedBy?.name || '?')[0].toUpperCase()}</div>
                          <div>
                            <div className="cust-name">{o.orderPlacedBy?.name || '—'}</div>
                            <div className="cust-sub">{o.orderPlacedBy?.phone || o.orderPlacedBy?.email || '—'}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="prod-tags">
                          {o.products?.slice(0, 2).map((p, pi) => <span key={pi} className="prod-tag">{p.productName} ×{p.quantity}</span>)}
                          {o.products?.length > 2 && <span className="prod-tag more">+{o.products.length - 2}</span>}
                        </div>
                      </td>
                      <td><span className="amt-cell">₹{o.totalAmount?.toLocaleString('en-IN')}</span></td>
                      <td>
                        <div className="pay-cell">
                          <span className="pay-method">{o.paymentMethod?.toUpperCase()}</span>
                          <span className="pay-status" style={{ color: pc.color, background: pc.bg }}>{pc.label}</span>
                        </div>
                      </td>
                      <td>
                        <span className="stat-pill" style={{ color: sc.color, background: sc.bg, borderColor: sc.border }}>{sc.label}</span>
                      </td>
                      <td>
                        {assigningOrder === o._id ? (
                          <select
                            autoFocus
                            style={{ fontSize: 11, padding: '2px 4px', borderRadius: 4 }}
                            onChange={e => handleAssignDelivery(o._id, e.target.value)}
                            onBlur={() => setAssigningOrder(null)}
                          >
                            <option value="">Select Partner…</option>
                            {deliveryBoys.map(d => (
                              <option key={d._id} value={d._id}>{d.name} ({d.contact || 'No phone'})</option>
                            ))}
                          </select>
                        ) : o.deliveryBoy ? (
                          <div className="del-cell" onClick={() => setAssigningOrder(o._id)} title="Click to reassign">
                            <span className="del-name">{o.deliveryBoy.name}</span>
                            <span className="del-email">{o.deliveryBoy.email || o.deliveryBoy.contact}</span>
                          </div>
                        ) : (
                          <button
                            className="btn btn-outline btn-sm"
                            style={{ fontSize: 11, padding: '2px 8px' }}
                            onClick={() => setAssigningOrder(o._id)}
                          >
                            Assign Partner
                          </button>
                        )}
                      </td>
                      <td className="date-cell">{new Date(o.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                      <td>
                        <button className={`expand-btn${isExp ? ' open' : ''}`} onClick={() => setExpanded(isExp ? null : o.orderId)}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9" /></svg>
                        </button>
                      </td>
                    </tr>
                    {isExp && (
                      <tr className="expand-row">
                        <td colSpan="10">
                          <div className="expand-inner">
                            <div className="expand-grid">
                              <div className="expand-section">
                                <h4>Products</h4>
                                <table className="prod-detail-tbl">
                                  <thead><tr><th>Product</th><th>Vendor</th><th>Qty</th><th>Price</th><th>Subtotal</th></tr></thead>
                                  <tbody>
                                    {o.products?.map((p, pi) => (
                                      <tr key={pi}>
                                        <td>{p.productName}</td>
                                        <td style={{ color: 'var(--t2)', fontSize: 11 }}>{p.vendor?.vendorName || p.vendor?.businessName || '—'}</td>
                                        <td>{p.quantity}</td>
                                        <td>₹{p.price?.toLocaleString('en-IN')}</td>
                                        <td><strong>₹{p.subtotal?.toLocaleString('en-IN')}</strong></td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                              <div>
                                <div className="meta-card">
                                  <h4>Customer &amp; Delivery Address</h4>
                                  <div className="meta-row"><span className="mk">Name</span><span className="mv">{o.orderPlacedBy?.name || '—'}</span></div>
                                  <div className="meta-row"><span className="mk">Email</span><span className="mv">{o.orderPlacedBy?.email || '—'}</span></div>
                                  <div className="meta-row"><span className="mk">Phone</span><span className="mv">{o.orderPlacedBy?.phone || '—'}</span></div>
                                  <div className="meta-row"><span className="mk">Address</span><span className="mv" style={{ fontSize: 11 }}>{addrStr}</span></div>
                                </div>
                                <div className="meta-card">
                                  <h4>Summary</h4>
                                  <div className="meta-row"><span className="mk">Method</span><span className="mv">{o.paymentMethod?.toUpperCase()}</span></div>
                                  <div className="meta-row"><span className="mk">Total</span><span className="mv" style={{ color: 'var(--orange)', fontWeight: 700 }}>₹{o.totalAmount?.toLocaleString('en-IN')}</span></div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}

        {!loading && filtered.length > 0 && (
          <div className="pagi">
            <span className="pagi-info">Showing {(page - 1) * LIMIT + 1}–{Math.min(page * LIMIT, filtered.length)} of {filtered.length}</span>
            <div className="pagi-btns">
              <button className="pagi-btn" disabled={page === 1} onClick={() => setPage(1)}>«</button>
              <button className="pagi-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹</button>
              {pageNums().map(p => <button key={p} className={`pagi-btn${p === page ? ' on' : ''}`} onClick={() => setPage(p)}>{p}</button>)}
              <button className="pagi-btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>›</button>
              <button className="pagi-btn" disabled={page === totalPages} onClick={() => setPage(totalPages)}>»</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderList;
